import React from 'react';
import { View, Text, ViewStyle } from 'react-native';

export interface BadgeProps {
  children: React.ReactNode;
  variant?:
    | 'default'
    | 'primary'
    | 'secondary'
    | 'success'
    | 'warning'
    | 'error'
    | 'brewing-method'
    | 'difficulty'
    | 'featured';
  size?: 'xs' | 'sm' | 'md' | 'lg';
  style?: ViewStyle;
  testID?: string;
}

export function Badge({
  children,
  variant = 'default',
  size = 'md',
  style,
  testID,
}: BadgeProps) {
  const getBackgroundColor = () => {
    const colors = {
      default: '#e5e7eb',
      primary: '#ea580c',
      secondary: '#6b7280',
      success: '#10b981',
      warning: '#f59e0b',
      error: '#ef4444',
      'brewing-method': 'rgba(139, 69, 19, 0.13)',
      difficulty: 'rgba(245, 158, 11, 0.13)',
      featured: 'rgba(251, 191, 36, 0.13)',
    };
    return colors[variant];
  };

  const getTextColor = () => {
    if (variant === 'default') return '#1f2937';
    if (variant === 'brewing-method') return '#8b4513';
    if (variant === 'difficulty') return '#f59e0b';
    if (variant === 'featured') return '#fbbf24';
    return '#ffffff';
  };

  const getPadding = () => {
    const paddings = {
      xs: { horizontal: 6, vertical: 2 },
      sm: { horizontal: 10, vertical: 2 },
      md: { horizontal: 12, vertical: 6 },
      lg: { horizontal: 16, vertical: 6 },
    };
    return paddings[size];
  };

  const getFontSize = () => {
    const sizes = { xs: 10, sm: 12, md: 14, lg: 16 };
    return sizes[size];
  };

  const padding = getPadding();

  return (
    <View
      style={[
        {
          backgroundColor: getBackgroundColor(),
          paddingHorizontal: padding.horizontal,
          paddingVertical: padding.vertical,
          borderRadius: 9999,
          alignSelf: 'flex-start',
          alignItems: 'center',
          justifyContent: 'center',
        },
        style,
      ]}
      testID={testID}
    >
      <Text
        style={{
          fontSize: getFontSize(),
          fontWeight: '500',
          color: getTextColor(),
          textAlign: 'center',
        }}
      >
        {children}
      </Text>
    </View>
  );
}

export interface DotBadgeProps {
  size?: number;
  color?: string;
  testID?: string;
}

export function DotBadge({
  size = 8,
  color = '#ef4444',
  testID,
}: DotBadgeProps) {
  return (
    <View
      style={{
        width: size,
        height: size,
        borderRadius: size / 2,
        backgroundColor: color,
      }}
      testID={testID}
    />
  );
}

export interface NumberBadgeProps {
  count: number;
  max?: number;
  size?: 'sm' | 'md' | 'lg';
  color?: string;
  testID?: string;
}

export function NumberBadge({
  count,
  max = 99,
  size = 'md',
  color = '#ef4444',
  testID,
}: NumberBadgeProps) {
  if (count === 0) return null;

  const displayCount = count > max ? `${max}+` : count.toString();
  const badgeSize = size === 'sm' ? 16 : size === 'md' ? 20 : 24;
  const fontSize = size === 'sm' ? 10 : size === 'md' ? 12 : 14;

  return (
    <View
      style={{
        minWidth: badgeSize,
        height: badgeSize,
        borderRadius: badgeSize / 2,
        backgroundColor: color,
        alignItems: 'center',
        justifyContent: 'center',
        paddingHorizontal: 4,
      }}
      testID={testID}
    >
      <Text
        style={{
          fontSize,
          fontWeight: 'bold',
          color: '#ffffff',
          textAlign: 'center',
        }}
      >
        {displayCount}
      </Text>
    </View>
  );
}
